import React, { useState, useEffect, useCallback, useRef } from "react";
import IssueSidebar, { IssueContextMenu } from "./IssueSidebar"; // adjust path if needed

/**
 * IssueSidebarBridge
 *
 * Mount once inside <Viewer />.
 *
 * Two open triggers:
 *  1. Toolbar buttons  → extension calls window.__issueSidebar.open({ issueTypeConfig })
 *  2. Right-click on the Autodesk viewer canvas → custom IssueContextMenu
 *
 * Key detail: Autodesk Viewer intercepts the native 'contextmenu' event and
 * shows its own menu. We disable that by:
 *   a) Calling viewer.setContextMenu(null) through the bridge once the viewer
 *      is ready (viewer instance is exposed via window.__autodeskViewer).
 *   b) Capturing the event at the capture phase (useCapture: true) so we run
 *      BEFORE the viewer's listener and calling stopImmediatePropagation().
 */
export const IssueSidebarBridge = () => {
  // ── Sidebar state ─────────────────────────────────────────────────────────
  const [isOpen,          setIsOpen]          = useState(false);
  const [issueTypeConfig, setIssueTypeConfig] = useState(null);

  // ── Context menu state ────────────────────────────────────────────────────
  const [ctxVisible, setCtxVisible] = useState(false);
  const [ctxX,       setCtxX]       = useState(0);
  const [ctxY,       setCtxY]       = useState(0);

  const issueTypeConfigsRef = useRef({});
  const viewerDisabledRef   = useRef(false); // track if viewer ctx menu is disabled

  // ── Register bridge handlers once extension is ready ──────────────────────
  useEffect(() => {
    let attempts = 0;

    const register = () => {
      const bridge = window.__issueSidebar;
      if (!bridge) {
        attempts++;
        if (attempts < 60) setTimeout(register, 500);
        else console.warn("IssueSidebarBridge: extension bridge not found.");
        return;
      }

      if (bridge.getIssueTypeConfigs) {
        issueTypeConfigsRef.current = bridge.getIssueTypeConfigs();
      }

      bridge.open = ({ issueTypeConfig: cfg } = {}) => {
        if (cfg) setIssueTypeConfig(cfg);
        setCtxVisible(false);
        setIsOpen(true);
      };

      bridge.close = () => setIsOpen(false);

      console.log("✅ IssueSidebarBridge: handlers registered");
    };

    register();
  }, []);

  // ── Disable Autodesk viewer's built-in context menu ───────────────────────
  useEffect(() => {
    /**
     * The Autodesk viewer attaches its own contextmenu listener.
     * We wait for the viewer instance (exposed at window.__autodeskViewer by
     * AutodeskViewer.jsx via the onViewerReady prop) then call
     * viewer.setContextMenu(null) to remove its custom context menu.
     *
     * Also set registerContextMenuCallback to a no-op so extensions can't
     * re-register a menu.
     */
    let attempts = 0;

    const disableViewerCtxMenu = () => {
      const viewer = window.__autodeskViewer;
      if (!viewer) {
        attempts++;
        if (attempts < 40) setTimeout(disableViewerCtxMenu, 750);
        return;
      }
      if (viewerDisabledRef.current) return;

      try {
        // Remove Autodesk's built-in context menu
        if (viewer.setContextMenu) {
          viewer.setContextMenu(null);
          console.log("✅ Autodesk viewer context menu disabled");
        }
        // Some versions use registerContextMenuCallback
        if (viewer.registerContextMenuCallback) {
          viewer.registerContextMenuCallback("issueOverride", () => {});
        }
        viewerDisabledRef.current = true;
      } catch (e) {
        console.warn("Could not disable viewer context menu:", e);
      }
    };

    disableViewerCtxMenu();
  }, []);

  // ── Right-click listener ──────────────────────────────────────────────────
  useEffect(() => {
    /**
     * We listen at the CAPTURE phase (third arg = true) so our handler fires
     * before Autodesk's bubble-phase listener. We then call
     * stopImmediatePropagation() to prevent the viewer's handler from running.
     *
     * Target: only fire when the click lands inside the viewer container.
     */
    const handleContextMenu = (e) => {
      const viewerRoot =
        document.querySelector(".adsk-viewing-viewer") ||
        document.querySelector(".viewer-element")      ||
        document.querySelector("[id^='LMV_']");        // Autodesk viewer root id pattern

      if (!viewerRoot || !viewerRoot.contains(e.target)) return;

      // Block Autodesk's own context menu
      e.preventDefault();
      e.stopImmediatePropagation();

      setCtxX(e.clientX);
      setCtxY(e.clientY);
      setCtxVisible(true);
    };

    // useCapture: true — fires before bubble-phase listeners
    document.addEventListener("contextmenu", handleContextMenu, true);
    return () => document.removeEventListener("contextmenu", handleContextMenu, true);
  }, []);

  // ── Context menu item selected ────────────────────────────────────────────
  const handleCtxSelect = useCallback(async (config) => {
    // For right-click flow, we must also ensure the pushpin extension is ready
    // and set the currentIssueType on the extension side.
    // We do this by going through the normal toolbar flow via the bridge.
    const bridge = window.__issueSidebar;
    if (bridge?.handleCreateClick) {
      // If extension exposes handleCreateClick directly, use it
      await bridge.handleCreateClick(config);
    } else {
      // Fallback: just open the sidebar (extension will receive formData via onSubmit)
      setIssueTypeConfig(config);
      setIsOpen(true);
    }
  }, []);

  // ── Panel close ───────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    setIsOpen(false);
    window.__issueSidebar?.onCancel?.();
  }, []);

  // ── Form submit ───────────────────────────────────────────────────────────
  // Close panel FIRST (without onCancel), then trigger pin placement.
  // Calling onCancel here would reset currentIssueType on the extension.
  const handleSubmit = useCallback((formData) => {
    setIsOpen(false);
    window.__issueSidebar?.onSubmit?.(formData);
  }, []);

  // ── Issue type configs for context menu ───────────────────────────────────
  const ctxConfigs = {
    QUALITY: issueTypeConfigsRef.current.QUALITY || { type: "issues", subtype: "quality_issue", defaultLabel: "Quality Issue", title: "Quality Issue", icon: "⚠️", color: "#FF9800", pinColor: "#FF9800" },
    SAFETY:  issueTypeConfigsRef.current.SAFETY  || { type: "issues", subtype: "safety_issue",  defaultLabel: "Safety Issue",  title: "Safety Issue",  icon: "🛡️", color: "#F44336", pinColor: "#F44336" },
    DESIGN:  issueTypeConfigsRef.current.DESIGN  || { type: "issues", subtype: "design_issue",  defaultLabel: "Design Issue",  title: "Design Issue",  icon: "📐", color: "#2196F3", pinColor: "#2196F3" },
    RFI:     issueTypeConfigsRef.current.RFI     || { type: "rfis",   subtype: "rfi",           defaultLabel: "RFI",           title: "RFI",           icon: "❓", color: "#4CAF50", pinColor: "#4CAF50" },
  };

  return (
    <>
      <IssueContextMenu
        visible={ctxVisible}
        x={ctxX}
        y={ctxY}
        onSelect={handleCtxSelect}
        onClose={() => setCtxVisible(false)}
        issueTypeConfigs={ctxConfigs}
      />

      <IssueSidebar
        isOpen={isOpen}
        onClose={handleClose}
        issueTypeConfig={issueTypeConfig}
        onSubmitForm={handleSubmit}
      />
    </>
  );
};
